import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { AlarmClock, Save } from "lucide-react";

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function formatHour(h) {
  const d = new Date();
  d.setHours(h, 0, 0, 0);
  return d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
}

export default function SettingsPreferences() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    close_reminder_enabled: true,
    close_reminder_threshold: 5000,
    close_reminder_hour: 21,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings", {
        close_reminder_enabled: settings.close_reminder_enabled,
        close_reminder_threshold: Number(settings.close_reminder_threshold),
        close_reminder_hour: Number(settings.close_reminder_hour),
      });
      setSettings(data);
      toast.success("Preferences saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-preferences">
      <div className="mb-6">
        <h2 className="font-heading font-bold text-xl text-espresso-900 tracking-tight">Preferences</h2>
        <p className="text-taupe-600 text-sm mt-1">Nudges and defaults for the whole cafe.</p>
      </div>

      <form onSubmit={save} className="card-soft p-6 sm:p-8 max-w-2xl space-y-6">
        {/* Close-of-business reminder */}
        <div className="pb-6 border-b border-taupe-200">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-espresso-100 text-espresso-800 flex items-center justify-center shrink-0">
                <AlarmClock className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-espresso-900 text-base tracking-tight">Close-of-business reminder</h3>
                <p className="text-taupe-600 text-sm mt-0.5">
                  Show a nudge on the dashboard if Cash at Cafe is above your threshold after your chosen hour.
                </p>
              </div>
            </div>
            <Switch
              checked={settings.close_reminder_enabled}
              onCheckedChange={(v) => setSettings((s) => ({ ...s, close_reminder_enabled: v }))}
              disabled={loading}
              data-testid="reminder-enabled-switch"
            />
          </div>

          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${!settings.close_reminder_enabled ? "opacity-50 pointer-events-none" : ""}`}>
            <div>
              <Label className="text-xs font-semibold text-taupe-600 mb-2 block uppercase tracking-wider">Threshold (₹)</Label>
              <Input
                type="number"
                min="0"
                step="100"
                value={settings.close_reminder_threshold}
                onChange={(e) => setSettings((s) => ({ ...s, close_reminder_threshold: e.target.value }))}
                className="rounded-xl h-11 bg-taupe-100 border-taupe-200"
                data-testid="reminder-threshold-input"
              />
              <p className="text-xs text-taupe-500 mt-1">
                Nudge fires when Cash at Cafe &gt; this amount.
              </p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-taupe-600 mb-2 block uppercase tracking-wider">After hour</Label>
              <select
                value={settings.close_reminder_hour}
                onChange={(e) => setSettings((s) => ({ ...s, close_reminder_hour: Number(e.target.value) }))}
                className="w-full rounded-xl h-11 bg-taupe-100 border border-taupe-200 px-3 text-sm text-espresso-900 focus:outline-none focus:ring-2 focus:ring-espresso-800/20"
                data-testid="reminder-hour-select"
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>{formatHour(h)}</option>
                ))}
              </select>
              <p className="text-xs text-taupe-500 mt-1">
                Local time. Cafe closes around here.
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saving || loading}
            className="rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold h-11 px-6"
            data-testid="save-preferences-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : "Save preferences"}
          </Button>
        </div>
      </form>
    </div>
  );
}
